/**
 * Login Page
 *
 * Page component for user authentication with:
 * - Google OAuth button
 * - Email/password login form
 * - Links to registration and password reset
 *
 * Story 1.4: OAuth Login with Google
 * Story 1.7: Email/Password Login
 */

import * as React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Input,
  Button,
} from "@provenleap/ui";
import { GoogleOAuthButton, OAuthDivider, PasswordInput } from "../../components/auth";
import { safeNext } from "../../lib/safe-next";
import { LoadingSpinner } from "../../components/icons";
import { useAuthStore } from "../../stores/authStore";
import { decodeJwtClaims } from "../../lib/auth";
import { router } from "../../router";

interface LoginPageProps {
  /** API base URL (defaults to current origin) */
  apiBaseUrl?: string;
  /** Called after successful login */
  onSuccess?: () => void;
}

/**
 * Format a duration in seconds as a human-readable string.
 * @param seconds - Duration in seconds
 * @returns Formatted string like "5 minutes" or "30 seconds"
 */
function formatDuration(seconds: number): string {
  if (seconds <= 0) return "a moment";

  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }

  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
}

/**
 * Format remaining lockout time as a human-readable string.
 * @param lockedUntil - Unix timestamp in milliseconds when lockout expires
 * @returns Formatted string like "5 minutes" or "30 seconds"
 */
function formatLockoutTime(lockedUntil: number): string {
  const remainingMs = lockedUntil - Date.now();
  if (remainingMs <= 0) return "a moment";

  const remainingSeconds = Math.ceil(remainingMs / 1000);
  return formatDuration(remainingSeconds);
}

export function LoginPage({ apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "", onSuccess }: LoginPageProps) {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  // Check for OAuth callback errors in URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");

    if (oauthError) {
      switch (oauthError) {
        case "oauth-denied":
          setError("Sign-in was cancelled. Please try again if you'd like to continue.");
          break;
        case "oauth-state-invalid":
          setError("Your sign-in session expired. Please try again.");
          break;
        case "oauth-provider-error":
        case "oauth-token-error":
          setError("We couldn't connect to Google. Please try again in a moment.");
          break;
        default:
          setError("An error occurred during sign-in. Please try again.");
      }

      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
    }

    // Check for verified status from email verification
    // Note: Could show a success toast/message here in the future
    const verified = params.get("verified");
    if (verified === "true") {
      // Clean up URL - success indication handled by UI state
      const url = new URL(window.location.href);
      url.searchParams.delete("verified");
      window.history.replaceState({}, "", url.toString());
    }

    // Check for password reset success message
    const message = params.get("message");
    if (message === "password-reset-success") {
      // Show success notification (we could use a toast here, but for now just note it)
      // The user will see a clean login page with the message that they can now log in
      const url = new URL(window.location.href);
      url.searchParams.delete("message");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Handle email/password login
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!email || !password) {
      setError("Please enter your email and password.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include", // Send/receive cookies
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();

        // Store access token in memory via auth store
        const { setAccessToken, setUser } = useAuthStore.getState();
        setAccessToken(data.data.access_token, data.data.expires_in);

        // Decode JWT to get user info
        const claims = decodeJwtClaims(data.data.access_token);
        if (claims) {
          setUser({
            id: claims.sub,
            email: claims.email,
            tenantId: claims.tenant_id,
            role: claims.role,
            plan: claims.plan,
          });
        }

        // Synchronously update router context so _authenticated beforeLoad
        // sees authenticated state during the navigation to /dashboard.
        // Without this, beforeLoad reads stale context and redirects back to /login.
        router.update({
          context: {
            auth: {
              isAuthenticated: true,
              isLoading: false,
              user: claims
                ? {
                    id: claims.sub,
                    email: claims.email,
                    tenantId: claims.tenant_id,
                    role: claims.role,
                    plan: claims.plan,
                  }
                : null,
            },
          },
        });

        // Navigate to dashboard
        onSuccess?.();
      } else {
        // The body may NOT be our JSON: a Cloudflare rate-limit / edge block
        // returns a 403 (or 429/503) HTML page. Parsing that as JSON used to
        // throw and fall through to the misleading "check your internet
        // connection" — so guard the parse and detect throttle statuses.
        let errorData: {
          type?: string;
          lockedUntil?: number;
          retryAfter?: number;
        } | null = null;
        try {
          errorData = await response.json();
        } catch {
          // non-JSON body (edge/proxy error page)
        }

        // Handle specific error types (RFC 9457 Problem Details)
        if (errorData?.type?.includes("invalid-credentials")) {
          setError("Invalid email or password. If you signed up with Google, please use the Google sign-in button.");
        } else if (errorData?.type?.includes("account-locked")) {
          const lockedUntil = errorData.lockedUntil;
          const timeRemaining = lockedUntil ? formatLockoutTime(lockedUntil) : "a few minutes";
          setError(`Too many failed attempts. Please try again in ${timeRemaining}.`);
        } else if (errorData?.type?.includes("rate-limit")) {
          const retryAfter = errorData.retryAfter;
          const timeRemaining = retryAfter ? formatDuration(retryAfter) : "a few minutes";
          setError(`Too many requests. Please try again in ${timeRemaining}.`);
        } else if (errorData?.type?.includes("validation-error")) {
          setError("Please check your email and password.");
        } else if (
          response.status === 429 ||
          response.status === 403 ||
          response.status === 503
        ) {
          // Throttle/edge block with a non-JSON body (e.g. Cloudflare rate
          // limit). Tell the truth instead of blaming the user's connection.
          setError("Too many attempts from your network. Please wait a few minutes, then try again.");
        } else {
          setError("Unable to sign in. Please try again in a moment.");
        }
      }
    } catch {
      setError("Unable to connect. Please try again in a moment (if this keeps happening after many attempts, wait a few minutes first).");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = () => {
    onSuccess?.();
  };

  return (
    <Card className="w-full !rounded-[22px] shadow-sm" style={{ border: "0.5px solid var(--hairline)" }}>
        <CardHeader className="pb-4">
          <CardTitle>Welcome!</CardTitle>
          <CardDescription className="text-neutral-700">Sign in to your account to continue</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Error message */}
          {error && (
            <div className="rounded-md bg-error/10 p-3 text-sm text-error">
              {error}
            </div>
          )}

          {/* OAuth buttons */}
          <GoogleOAuthButton
            apiBaseUrl={apiBaseUrl}
            returnUrl={
              safeNext(new URLSearchParams(window.location.search).get("next")) ?? "/clients"
            }
            onClick={handleGoogleSuccess}
          />

          {/* Divider */}
          <OAuthDivider text="Or continue with email" />

          {/* Email/password form */}
          <form onSubmit={handleSubmit} className="space-y-2">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
              </div>
              <Input
                id="email"
                type="email"
                className="rounded-lg"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <a
                  href="/forgot-password"
                  className="text-[13px] font-medium text-primary hover:underline"
                >
                  Forgot password?
                </a>
              </div>
              <PasswordInput
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            <Button type="submit" variant="brand" className="w-full mt-4 cursor-pointer" disabled={isLoading}>
              {isLoading ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>
        </CardContent>

        <CardFooter className="flex justify-center">
          <p className="text-sm text-neutral-700">
            Don't have an account?{" "}
            <a href="/register" className="font-medium text-primary hover:underline">
              Sign up
            </a>
          </p>
        </CardFooter>
    </Card>
  );
}
